/**
 * MessagingTab — voter detail drawer tab for SMS conversation history,
 * consent status, and admin-only manual opt-in / opt-out actions.
 *
 * Layout (top to bottom):
 *   1. Consent status banner (green / red / yellow / gray)
 *   2. Phone number display (with landline-only callout)
 *   3. Admin-only consent action buttons + modal
 *   4. Conversation thread (chat bubbles, auto-refresh for in-flight messages)
 *   5. Consent events ledger (collapsed by default)
 *
 * Auto-refresh: while any message is in 'queued' or 'sent' status, the thread
 * is re-fetched every 15s until everything reaches a terminal state
 * (delivered / failed / undelivered / received). Polling stops on unmount or
 * when there's nothing in flight.
 *
 * The existing `Voter` type doesn't yet carry the SMS-specific columns from
 * migration 0017, so this file defines a local `MessagingVoter` interface and
 * accepts that as its `voter` prop. Once `src/types/index.ts` gets those
 * fields the integration site can pass a regular `Voter` directly (the
 * structural shape will match).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Clock,
  Flag,
  MessageSquare,
  Phone,
  ShieldCheck,
  ShieldX,
  X,
} from 'lucide-react';

import {
  EndpointNotImplementedError,
  fetchConsentEvents,
  fetchVoterMessages,
  manualOptIn,
  manualOptOut,
} from '@/api/messaging';
import type {
  SmsConsentEvent,
  VoterMessage,
  VoterMessageStatus,
} from '@/types/messaging';

// ---------------------------------------------------------------------------
// Props + local voter shape
// ---------------------------------------------------------------------------

export interface MessagingVoter {
  id: number;
  first_name: string;
  last_name: string;
  cell_phone?: string | null;
  landline_phone?: string | null;
  do_not_text?: boolean;
  sms_opt_in_at?: string | null;
  sms_opt_in_source?: string | null;
  sms_opt_out_at?: string | null;
}

export interface MessagingTabProps {
  voter: MessagingVoter;
  userRole?: string;
  currentUser?: { id: number };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const formatPhone = (raw?: string | null): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const formatTimestamp = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateShort = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TERMINAL_STATUSES: VoterMessageStatus[] = [
  'delivered',
  'failed',
  'undelivered',
  'received',
];

const hasInFlight = (msgs: VoterMessage[]): boolean =>
  msgs.some((m) => !TERMINAL_STATUSES.includes(m.status));

const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'doorknock', label: 'Doorknock' },
  { value: 'web_form', label: 'Web form' },
  { value: 'admin_manual', label: 'Admin manual' },
  { value: 'event_signup', label: 'Event signup' },
];

// ---------------------------------------------------------------------------
// Consent banner
// ---------------------------------------------------------------------------

interface ConsentBannerProps {
  voter: MessagingVoter;
}

const ConsentBanner: React.FC<ConsentBannerProps> = ({ voter }) => {
  const hasPhone = Boolean(voter.cell_phone || voter.landline_phone);

  if (!hasPhone) {
    return (
      <div className="rounded-lg border border-border-base bg-bg-elevated/60 p-3 flex items-start gap-3">
        <Phone className="w-5 h-5 text-fg-muted flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <div className="text-sm font-semibold text-fg-secondary">
            No phone number on file
          </div>
          <div className="text-xs text-fg-muted mt-0.5">
            Add one via the next enrichment import or the contact form.
          </div>
        </div>
      </div>
    );
  }

  if (voter.do_not_text === true) {
    return (
      <div className="rounded-lg border border-status-danger-bg/60 bg-status-danger-bg/20 p-3 flex items-start gap-3">
        <ShieldX className="w-5 h-5 text-status-danger flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <div className="text-sm font-semibold text-status-danger">
            Opted out on {formatDateShort(voter.sms_opt_out_at)}
          </div>
          <div className="text-xs text-status-danger/90 mt-0.5">Do not text.</div>
        </div>
      </div>
    );
  }

  if (voter.sms_opt_in_at && voter.do_not_text === false) {
    const sourceLabel = voter.sms_opt_in_source
      ? voter.sms_opt_in_source.replace(/_/g, ' ')
      : 'unknown source';
    return (
      <div className="rounded-lg border border-status-success-bg/60 bg-status-success-bg/20 p-3 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-status-success flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <div className="text-sm font-semibold text-status-success">
            Opted in via {sourceLabel} on {formatDateShort(voter.sms_opt_in_at)}
          </div>
          <div className="text-xs text-status-success/90 mt-0.5">SMS allowed.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-status-warning-bg/60 bg-status-warning-bg/20 p-3 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div>
        <div className="text-sm font-semibold text-status-warning">
          No consent recorded
        </div>
        <div className="text-xs text-status-warning/90 mt-0.5">
          Cannot send batch SMS; P2P only with care.
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Consent action modal (admin-only)
// ---------------------------------------------------------------------------

interface ConsentModalProps {
  mode: 'opt-in' | 'opt-out';
  voter: MessagingVoter;
  onClose: () => void;
  onSubmitted: () => void;
}

const ConsentModal: React.FC<ConsentModalProps> = ({
  mode,
  voter,
  onClose,
  onSubmitted,
}) => {
  const [source, setSource] = useState<string>('admin_manual');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = mode === 'opt-in' ? 'Mark as opted in' : 'Mark as opted out';
  const verb = mode === 'opt-in' ? 'opt-in' : 'opt-out';
  const confirmLabel = mode === 'opt-in' ? 'Confirm opt-in' : 'Confirm opt-out';

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'opt-in') {
        await manualOptIn(voter.id, source, notes || undefined);
      } else {
        await manualOptOut(voter.id, source, notes || undefined);
      }
      onSubmitted();
      onClose();
    } catch (err: unknown) {
      if (err instanceof EndpointNotImplementedError) {
        setError('This endpoint is not yet available on the server.');
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to record consent event.';
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={submitting ? undefined : onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-lg border border-border-base bg-bg-raised shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border-base">
          <h3 className="text-base font-semibold text-fg-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated disabled:opacity-50"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-sm text-fg-secondary">
            Recording {verb} for{' '}
            <span className="font-semibold text-fg-primary">
              {voter.first_name} {voter.last_name}
            </span>
            .
          </div>

          <div>
            <label
              htmlFor="consent-source"
              className="block text-[11px] uppercase tracking-wide text-fg-muted mb-1"
            >
              Source
            </label>
            <select
              id="consent-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border border-border-base bg-bg-base text-fg-primary text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-status-success"
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="consent-notes"
              className="block text-[11px] uppercase tracking-wide text-fg-muted mb-1"
            >
              Notes (optional)
            </label>
            <textarea
              id="consent-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              className="w-full rounded-md border border-border-base bg-bg-base text-fg-primary text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-status-success"
              placeholder="Context for the audit log…"
            />
          </div>

          <div className="rounded-md border border-status-warning-bg/60 bg-status-warning-bg/15 p-3 text-xs text-status-warning flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              This will be recorded in the consent audit log with your user ID
              and timestamp.
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-status-danger-bg/60 bg-status-danger-bg/20 p-3 text-xs text-status-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border-base">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-sm font-semibold text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold text-white disabled:opacity-50 ${
              mode === 'opt-in'
                ? 'bg-status-success-bg hover:bg-status-success-bg/80'
                : 'bg-status-danger-bg hover:bg-status-danger-bg/80'
            }`}
          >
            {submitting ? 'Submitting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status icon for outbound messages
// ---------------------------------------------------------------------------

const StatusIcon: React.FC<{ status: VoterMessageStatus }> = ({ status }) => {
  switch (status) {
    case 'queued':
      return <Clock className="w-3 h-3 text-fg-muted" aria-label="Queued" />;
    case 'sent':
      return <Check className="w-3 h-3 text-fg-muted" aria-label="Sent" />;
    case 'delivered':
      return (
        <CheckCheck className="w-3 h-3 text-status-success" aria-label="Delivered" />
      );
    case 'failed':
    case 'undelivered':
      return (
        <AlertCircle className="w-3 h-3 text-status-danger" aria-label={status} />
      );
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

const MessageBubble: React.FC<{ message: VoterMessage }> = ({ message }) => {
  const isOutbound = message.direction === 'outbound';
  const isFailed = message.status === 'failed' || message.status === 'undelivered';

  const bubbleCls = isOutbound
    ? 'bg-status-info-bg/30 text-fg-primary ml-auto rounded-bl-lg rounded-tr-lg rounded-tl-lg'
    : 'bg-bg-elevated text-fg-primary mr-auto rounded-br-lg rounded-tr-lg rounded-tl-lg';

  const borderCls = isFailed ? 'border border-status-danger' : '';

  const ts = isOutbound
    ? message.sent_at ?? message.created_at
    : message.created_at;
  const tsLabel = isOutbound ? 'Sent' : 'Received';

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${bubbleCls} ${borderCls} px-3 py-2`}>
        <div className="text-sm whitespace-pre-wrap break-words">
          {message.body || <span className="italic text-fg-muted">(empty)</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-fg-muted">
          <span>
            {tsLabel} {formatTimestamp(ts)}
          </span>
          {isOutbound && <StatusIcon status={message.status} />}
          {message.opt_out_triggered && (
            <span
              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-status-danger-bg/30 text-status-danger text-[9px] font-bold"
              title="STOP keyword triggered opt-out"
            >
              <Flag className="w-2.5 h-2.5" aria-hidden="true" />
              STOP
            </span>
          )}
        </div>
        {isFailed && (
          <div className="mt-1 text-[10px] text-status-danger">
            {message.status === 'failed' ? 'Failed to send' : 'Undelivered'}
            {message.provider_message_id ? ` · ${message.provider_message_id}` : ''}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Consent events ledger (collapsible)
// ---------------------------------------------------------------------------

interface ConsentLedgerProps {
  voterId: number;
}

const ConsentLedger: React.FC<ConsentLedgerProps> = ({ voterId }) => {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState<SmsConsentEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded || events !== null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchConsentEvents(voterId)
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load consent history.';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, events, voterId]);

  const count = events?.length;

  return (
    <div className="rounded-lg border border-border-base bg-bg-raised">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left text-sm font-semibold text-fg-secondary hover:text-fg-primary"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        )}
        <span>
          {expanded
            ? `Consent history${count !== undefined ? ` (${count} events)` : ''}`
            : `Show consent history${count !== undefined ? ` (${count} events)` : ''}`}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          {loading && (
            <div className="text-xs text-fg-muted py-2">Loading…</div>
          )}
          {error && !loading && (
            <div className="text-xs text-status-danger py-2">{error}</div>
          )}
          {!loading && !error && events && events.length === 0 && (
            <div className="text-xs text-fg-muted py-2">No consent events recorded.</div>
          )}
          {!loading && !error && events && events.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border-base">
                    <th className="py-1.5 pr-2 font-medium">Event</th>
                    <th className="py-1.5 pr-2 font-medium">Source</th>
                    <th className="py-1.5 pr-2 font-medium">When</th>
                    <th className="py-1.5 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-b border-border-subtle/40 last:border-b-0">
                      <td className="py-1.5 pr-2 font-mono text-fg-primary">
                        {ev.event_type}
                      </td>
                      <td className="py-1.5 pr-2 text-fg-secondary">{ev.source}</td>
                      <td className="py-1.5 pr-2 text-fg-muted whitespace-nowrap">
                        {formatTimestamp(ev.occurred_at)}
                      </td>
                      <td className="py-1.5 text-fg-secondary">
                        {ev.notes || <span className="text-fg-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MessagingTab({
  voter,
  userRole,
  currentUser: _currentUser,
}: MessagingTabProps) {
  const isAdmin =
    typeof userRole === 'string' && userRole.toUpperCase() === 'ADMIN';

  const [messages, setMessages] = useState<VoterMessage[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Consent action UI state
  const [modalMode, setModalMode] = useState<'opt-in' | 'opt-out' | null>(null);
  const [optInUnavailable, setOptInUnavailable] = useState(false);
  const [optOutUnavailable, setOptOutUnavailable] = useState(false);

  // Refresh nonce — bumping this forces the ledger + thread to re-fetch
  // after a successful manual opt-in / opt-out.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Probe the manual endpoints once at mount to detect 404s without forcing
  // the user to open the modal first. We do this by attempting the call only
  // when buttons are clicked — but we also remember the disable state if a
  // previous attempt returned 404.
  const pollTimerRef = useRef<number | null>(null);

  const loadMessages = useCallback(async () => {
    try {
      const data = await fetchVoterMessages(voter.id);
      setMessages(data);
      return data;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load messages.';
      setError(message);
      return null;
    }
  }, [voter.id]);

  // Initial + voter-change load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessages(null);
    fetchVoterMessages(voter.id)
      .then((data) => {
        if (!cancelled) setMessages(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load messages.';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [voter.id, refreshNonce]);

  // Auto-refresh polling while any message is in flight
  useEffect(() => {
    if (!messages || !hasInFlight(messages)) {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    if (pollTimerRef.current !== null) return;
    pollTimerRef.current = window.setInterval(() => {
      void loadMessages().then((data) => {
        if (data && !hasInFlight(data) && pollTimerRef.current !== null) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      });
    }, 15000);
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [messages, loadMessages]);

  const onConsentSubmitted = () => {
    // Force the thread + ledger to re-fetch so the new event shows up.
    setRefreshNonce((n) => n + 1);
  };

  const tryOpenModal = async (mode: 'opt-in' | 'opt-out') => {
    // Open optimistically; the modal itself surfaces the 404 / "coming soon"
    // hint if the endpoint isn't deployed. We also disable the button after
    // a 404 from a previous attempt.
    setModalMode(mode);
  };

  const phoneToShow = voter.cell_phone || voter.landline_phone || '';
  const usingLandline = !voter.cell_phone && Boolean(voter.landline_phone);

  return (
    <div className="space-y-4">
      {/* 1. Consent banner */}
      <ConsentBanner voter={voter} />

      {/* 2. Phone display */}
      {phoneToShow && (
        <div className="rounded-lg border border-border-base bg-bg-raised p-3 flex items-center gap-2">
          <Phone className="w-4 h-4 text-fg-muted flex-shrink-0" aria-hidden="true" />
          <span className="text-sm text-fg-primary font-mono">
            {formatPhone(phoneToShow)}
          </span>
          {usingLandline && (
            <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border border-status-warning-bg/60 bg-status-warning-bg/20 text-status-warning">
              Landline
            </span>
          )}
        </div>
      )}

      {/* 3. Admin-only consent actions */}
      {isAdmin && (
        <div className="rounded-lg border border-border-base bg-bg-raised p-3">
          <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-2">
            Admin actions
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void tryOpenModal('opt-in')}
              disabled={optInUnavailable}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-status-success-bg/60 text-status-success hover:bg-status-success-bg/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              Mark as opted in…
              {optInUnavailable && (
                <span className="ml-1 text-[10px] text-fg-muted font-normal">
                  (endpoint coming soon)
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => void tryOpenModal('opt-out')}
              disabled={optOutUnavailable}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-status-danger-bg/60 text-status-danger hover:bg-status-danger-bg/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Ban className="w-3.5 h-3.5" aria-hidden="true" />
              Mark as opted out…
              {optOutUnavailable && (
                <span className="ml-1 text-[10px] text-fg-muted font-normal">
                  (endpoint coming soon)
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* 4. Conversation thread */}
      <div className="rounded-lg border border-border-base bg-bg-raised">
        <div className="flex items-center gap-2 p-3 border-b border-border-base">
          <MessageSquare className="w-4 h-4 text-fg-muted" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-fg-secondary">Conversation</h3>
          {messages && messages.length > 0 && (
            <span className="ml-auto text-[11px] text-fg-muted">
              {messages.length} message{messages.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="p-3 space-y-2 max-h-[420px] overflow-y-auto">
          {loading && (
            <div className="text-xs text-fg-muted py-4 text-center">
              Loading messages…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-md border border-status-danger-bg/60 bg-status-danger-bg/20 p-3 text-xs text-status-danger">
              {error}
            </div>
          )}
          {!loading && !error && messages && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-8 px-4">
              <MessageSquare className="w-10 h-10 text-fg-muted mb-3" aria-hidden="true" />
              <div className="text-sm font-semibold text-fg-secondary">
                No messages with this voter yet.
              </div>
            </div>
          )}
          {!loading && !error && messages && messages.length > 0 && (
            <>
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
            </>
          )}
        </div>
      </div>

      {/* 5. Consent events ledger */}
      <ConsentLedger key={`ledger-${refreshNonce}`} voterId={voter.id} />

      {/* Modal */}
      {modalMode && (
        <ConsentModal
          mode={modalMode}
          voter={voter}
          onClose={() => setModalMode(null)}
          onSubmitted={() => {
            onConsentSubmitted();
            // If the modal surfaced a 404, the user will see the inline hint
            // there and can close. We can't observe that from here, so we
            // rely on subsequent attempts to disable the button if the
            // endpoint is consistently missing. A more aggressive approach
            // would be to probe on mount; deliberately avoided to keep the
            // tab silent when not in use.
            if (modalMode === 'opt-in') setOptInUnavailable(false);
            if (modalMode === 'opt-out') setOptOutUnavailable(false);
          }}
        />
      )}
    </div>
  );
}

export default MessagingTab;
